import React, { useEffect, useCallback } from 'react';
import '../styles/components/Campaign.css';

// localStorageのキー
const STORAGE_KEY = 'campaignModalShown_testPlayer2026';

/**
 * キャンペーンモーダルコンポーネント
 * 初回ログイン時にキャンペーン内容を表示
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - モーダル表示状態
 * @param {Function} props.onClose - モーダルを閉じるコールバック
 * @param {Object} props.campaign - キャンペーン情報 { active, name, endDate, feedbackUrl }
 */
const CampaignModal = ({ isOpen, onClose, campaign }) => {
  // モーダルを閉じる処理
  const handleClose = useCallback(() => {
    // 表示済みフラグを保存
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      // localStorage使用不可の場合は無視
    }
    onClose();
  }, [onClose]);

  // ESCキーで閉じる
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // スクロール防止
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose]);

  // 日付フォーマット
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}年${month}月${day}日`;
  };

  // モーダルが閉じている場合は非表示
  if (!isOpen || !campaign) {
    return null;
  }

  // 利用可能な機能リスト
  const features = [
    'つむぎチャット（体調の相談・雑談）',
    '栄養相談フォーム（詳しく相談）',
    '冷蔵庫・チラシ写真からレシピ作成',
    '食材・成分表チェック（ヨウ素・セレン）',
    '気分の記録・相談履歴の保存'
  ];

  return (
    <div
      className="campaign-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="campaign-modal-title"
    >
      <div
        className="campaign-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="campaign-modal__header">
          <span className="campaign-modal__header-icon" aria-hidden="true">
            🎉
          </span>
          <h2 id="campaign-modal-title" className="campaign-modal__title">
            一緒に、つくってみない？
          </h2>
          <p className="campaign-modal__subtitle">
            2月末まで、全部使えるよ
          </p>
        </div>

        {/* コンテンツ */}
        <div className="campaign-modal__content">
          <p className="campaign-modal__description">
            使ってくれて、ありがとう。<br />
            つむぎをもっと良くしたいから、感想を聞かせてくれたら嬉しいな。
          </p>

          {/* 機能リスト */}
          <div className="campaign-modal__features">
            <h3 className="campaign-modal__features-title">
              <span className="campaign-modal__features-title-icon" aria-hidden="true">
                🌸
              </span>
              利用できる機能
            </h3>
            <ul className="campaign-modal__feature-list">
              {features.map((feature, index) => (
                <li key={index} className="campaign-modal__feature-item">
                  <span className="campaign-modal__feature-check" aria-hidden="true">
                    ✓
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* 期間表示 */}
          {campaign.endDate && (
            <div className="campaign-modal__period">
              <p className="campaign-modal__period-label">
                キャンペーン期間
              </p>
              <p className="campaign-modal__period-date">
                {formatDate(campaign.endDate)}まで
              </p>
            </div>
          )}

          {/* CTAボタン */}
          <button
            className="campaign-modal__cta"
            onClick={handleClose}
            autoFocus
          >
            <span className="campaign-modal__cta-icon" aria-hidden="true">
              💖
            </span>
            使ってみる
          </button>

          <p className="campaign-modal__note">
            使ってみた感想、聞かせてね。
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * モーダルを表示すべきかどうかを判定するヘルパー関数
 * @returns {boolean} 表示すべきならtrue
 */
export const shouldShowCampaignModal = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'true';
  } catch (e) {
    // localStorage使用不可の場合は表示する
    return true;
  }
};

export default CampaignModal;
